import { create } from 'zustand';
import {
  getQuestions,
  getAssessments,
  getAvailableQuestions,
  getCarveAssessmentSources,
  Question,
  Assessment,
  AvailableQuestion,
  CarveAssessmentSource,
} from '@/services/PagesServices/AssessmentManagementService';

interface AssessmentManagementState {
  questions: Question[] | null;
  assessments: Assessment[] | null;
  availableQuestions: AvailableQuestion[] | null;
  carveAssessmentSources: CarveAssessmentSource[] | null;
  loading: boolean;
  error: string | null;

  fetchQuestions: () => Promise<void>;
  fetchAssessments: () => Promise<void>;
  fetchAvailableQuestions: () => Promise<void>;
  fetchCarveAssessmentSources: () => Promise<void>;

  // Question actions
  addQuestion: (question: Question) => void;
  deleteQuestion: (questionId: number) => void;
}

const useAssessmentManagementStore = create<AssessmentManagementState>((set, get) => ({
  questions: null,
  assessments: null,
  availableQuestions: null,
  carveAssessmentSources: null,
  loading: false,
  error: null,

  fetchQuestions: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getQuestions();
      set({ questions: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAssessments: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAssessments();
      set({ assessments: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAvailableQuestions: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAvailableQuestions();
      set({ availableQuestions: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCarveAssessmentSources: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCarveAssessmentSources();
      set({ carveAssessmentSources: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  addQuestion: (question: Question) => {
    const { questions } = get();
    if (questions) {
      set({ questions: [...questions, question] });
    }
  },

  deleteQuestion: (questionId: number) => {
    const { questions } = get();
    if (questions) {
      set({ questions: questions.filter(q => q.id !== questionId) });
    }
  },
}));

export default useAssessmentManagementStore;
